import { computed, shallowRef, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  type Middleware,
  type Placement,
  type VirtualElement,
} from '@floating-ui/vue';

export interface FloatingPanelOptions {
  open?: MaybeRefOrGetter<boolean>;
  placement?: MaybeRefOrGetter<Placement>;
  offset?: MaybeRefOrGetter<number>;
  padding?: MaybeRefOrGetter<number>;
  arrowEl?: Ref<HTMLElement | null>;
  /**
   * Hand the panel the height left on its side of the anchor as the custom
   * property `--floating-available-height` (px), so a long list can cap its
   * `max-height` with it and scroll instead of running off the screen.
   */
  fitHeight?: MaybeRefOrGetter<boolean>;
  /**
   * The area the panel keeps inside instead of the whole viewport — a
   * spreadsheet's grid, so a cell's list never covers the sheet tabs below it.
   * Flipping, shifting and `fitHeight` all measure against it.
   */
  boundary?: MaybeRefOrGetter<HTMLElement | null | undefined>;
}

/** The custom property `fitHeight` sets on the panel. */
export const FLOATING_AVAILABLE_HEIGHT = '--floating-available-height';

/**
 * Pure middleware builder — no DOM/layout access, so it is unit-testable.
 * Order is load-bearing: offset → flip → (size) → shift → arrow.
 */
export function buildMiddleware(opts: {
  offset: number;
  padding: number;
  arrowEl?: Ref<HTMLElement | null>;
  fitHeight?: boolean;
  boundary?: HTMLElement | null;
}): Middleware[] {
  const area = opts.boundary ? { boundary: opts.boundary } : {};
  const middleware: Middleware[] = [offset(opts.offset), flip(area)];
  if (opts.fitHeight) {
    middleware.push(
      size({
        ...area,
        padding: opts.padding,
        apply({ availableHeight, elements }) {
          elements.floating.style.setProperty(
            FLOATING_AVAILABLE_HEIGHT,
            `${Math.max(0, Math.floor(availableHeight))}px`
          );
        },
      })
    );
  }
  middleware.push(shift({ ...area, padding: opts.padding }));
  if (opts.arrowEl) {
    middleware.push(arrow({ element: opts.arrowEl }));
  }
  return middleware;
}

/**
 * Positions `floating` against `reference` in the viewport (strategy 'fixed',
 * for top-layer popovers). Recomputes on scroll/resize via autoUpdate.
 * Shared by menus, popovers, tooltips and completion panels. Keep their
 * positioning transform free of animations and transitions.
 */
export function useFloatingPanel(
  reference: Ref<HTMLElement | VirtualElement | null>,
  floating: Ref<HTMLElement | null>,
  options: FloatingPanelOptions = {}
) {
  const refresh = shallowRef(0);
  let sequence = 0;
  const configuration = computed(() => {
    void refresh.value;
    const element = floating.value;
    // Reading both refs invalidates an earlier measurement when a menu is
    // retargeted or its panel is replaced, even while it stays open.
    const anchor = reference.value;
    const open = toValue(options.open) ?? element !== null;
    const placement = toValue(options.placement) ?? 'bottom';
    const ready = Boolean(open && anchor && element && topLayerReady(element));
    const version = ++sequence;
    const middleware = buildMiddleware({
      offset: toValue(options.offset) ?? 8,
      padding: toValue(options.padding) ?? 8,
      arrowEl: options.arrowEl,
      fitHeight: toValue(options.fitHeight) ?? false,
      boundary: toValue(options.boundary) ?? null,
    });
    // Captured when the measurement starts, rather than when its async
    // work finishes: showPopover may change the containing block in between.
    middleware.push({ name: 'nxPanelPosition', fn: () => ({ data: { version, ready } }) });
    return { version, open, placement, middleware };
  });

  const {
    floatingStyles: positionStyles,
    placement: measuredPlacement,
    middlewareData: measuredData,
  } = useFloating(reference, floating, {
    open: () => configuration.value.open,
    placement: () => configuration.value.placement,
    strategy: 'fixed',
    // Position with left/top, so a panel's transform animation cannot override
    // its coordinates and move it through the viewport origin.
    transform: false,
    whileElementsMounted: (anchor, element) => {
      const update = () => {
        refresh.value += 1;
      };
      element.addEventListener('toggle', update);
      const cleanup = autoUpdate(anchor, element, update);
      return () => {
        element.removeEventListener('toggle', update);
        cleanup();
      };
    },
    middleware: () => configuration.value.middleware,
  });

  const accepted = shallowRef<{
    version: number;
    styles: typeof positionStyles.value;
    placement: typeof measuredPlacement.value;
    data: typeof measuredData.value;
  } | null>(null);
  // Floating UI commits middlewareData after its coordinates. Watch this
  // completion signal, not each coordinate separately, and keep the last
  // accepted snapshot if an overtaken promise finishes afterwards.
  watch(
    measuredData,
    (data) => {
      const stamp = data.nxPanelPosition as { version?: number; ready?: boolean } | undefined;
      if (!stamp?.ready || stamp.version !== configuration.value.version) return;
      accepted.value = {
        version: stamp.version,
        styles: { ...positionStyles.value },
        placement: measuredPlacement.value,
        data,
      };
    },
    { flush: 'sync' }
  );

  const isPositioned = computed(
    () => configuration.value.open && accepted.value?.version === configuration.value.version
  );
  // Opacity keeps the panel measurable and focusable while its first valid
  // top-layer position is pending. An old result cannot reveal it at (0,0).
  const floatingStyles = computed(() => ({
    ...(accepted.value?.styles ?? positionStyles.value),
    opacity: isPositioned.value ? undefined : 0,
  }));
  const placement = computed(() => accepted.value?.placement ?? measuredPlacement.value);
  const middlewareData = computed(() => accepted.value?.data ?? {});
  const update = () => {
    refresh.value += 1;
  };

  return { floatingStyles, placement, middlewareData, isPositioned, update };
}

function topLayerReady(element: HTMLElement): boolean {
  if (!element.hasAttribute('popover') || !('showPopover' in element)) return true;
  try {
    return element.matches(':popover-open');
  } catch {
    // Non-browser renderers may stub showPopover without implementing the
    // pseudo-class. Their panels have no native hidden/top-layer state.
    return true;
  }
}
